import React, { useState, useEffect } from "react";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import CannedResponseChips from "./CannedResponseChips";
import { APPLICATION_FEEDBACK_CANNED_RESPONSES } from "./cannedReviewResponses";

const APPLICATION_FIELDS = [
  { key: "name", label: "Full Name" },
  { key: "nickname", label: "In-game Nickname" },
  { key: "dateOfBirth", label: "Date of Birth" },
  { key: "country", label: "Country" },
  { key: "steamId64", label: "Steam ID 64" },
  { key: "steamProfileUrl", label: "Steam Profile URL" },
  { key: "faceitProfileUrl", label: "FaceIT Profile URL" },
  { key: "phoneNumber", label: "Phone Number" },
  { key: "passport", label: "Identity Document (Passport)" },
  { key: "selfie_video", label: "Selfie Video" },
];

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  checkboxGroup: {
    marginBottom: theme.spacing(1),
  },
  messageField: {
    marginTop: theme.spacing(1),
  },
  statusNote: {
    marginTop: theme.spacing(1.5),
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
}));

export default function SendApplicationFeedbackDialog({
  openState,
  userContext,
  onClose,
  onSend,
}) {
  const classes = useStyles();
  const [selectedFields, setSelectedFields] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (openState) {
      setSelectedFields([]);
      setMessage("");
      setSending(false);
      setError("");
      setSuccess(false);
    }
  }, [openState]);

  if (!("row" in userContext)) {
    return <></>;
  }

  const { row } = userContext;

  const toggleField = (key) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };

  const canSend = (selectedFields.length > 0 || message.trim().length > 0) && !sending;

  const handleSend = async () => {
    setSending(true);
    setError("");
    setSuccess(false);
    try {
      await onSend({
        userId: row.userId,
        flaggedFields: selectedFields,
        message: message.trim(),
      });
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || "Failed to send feedback");
      setSending(false);
    }
  };

  return (
    <Dialog
      open={openState}
      onClose={onClose}
      aria-labelledby="app-feedback-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="app-feedback-dialog-title">
        Send Application Feedback — {row.nickname || row.email}
      </DialogTitle>

      <DialogContent>
        <DialogContentText>
          Select the field(s) that need attention and optionally add a message.
          An email will be sent to the user explaining what to fix and how to
          re-submit their application. Their account will be unlocked for
          editing.
        </DialogContentText>

        <Typography className={classes.sectionTitle} variant="body2">
          Flagged fields (select all that apply):
        </Typography>

        <FormGroup className={classes.checkboxGroup}>
          {APPLICATION_FIELDS.map(({ key, label }) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  checked={selectedFields.includes(key)}
                  onChange={() => toggleField(key)}
                  color="primary"
                  size="small"
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        <Typography className={classes.sectionTitle} variant="body2">
          Additional message (optional):
        </Typography>

        <CannedResponseChips
          presets={APPLICATION_FEEDBACK_CANNED_RESPONSES}
          value={message}
          onToggle={({ preset, nextValue, inserted }) => {
            setMessage(nextValue);
            if (inserted && Array.isArray(preset.flaggedFields)) {
              setSelectedFields((prev) => {
                const next = new Set(prev);
                preset.flaggedFields.forEach((field) => next.add(field));
                return Array.from(next);
              });
            }
          }}
        />

        <TextField
          className={classes.messageField}
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          variant="outlined"
          placeholder="Add any specific notes or instructions for the user..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        {row.accountStatus === "pending_review" && (
          <Typography className={classes.statusNote}>
            This user is currently in <strong>pending review</strong>. Sending
            this feedback will reset their status back to <strong>pending</strong>{" "}
            so they can edit and re-submit.
          </Typography>
        )}

        {error && (
          <Alert severity="error" style={{ marginTop: 12 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" style={{ marginTop: 12 }}>
            Feedback sent successfully.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" disabled={sending}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={!canSend}
        >
          {sending ? "Sending..." : "Send Feedback"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
